/**
 * mSTEM UC-47 "Quản lý khối mở rộng" manager container (MOCK-ONLY).
 * Lists user-declared blocks from mstem.customBlocks in an MstemTable and
 * wires the three management transactions:
 *   - S-1 search: filters the list by name (local state, ExtManagerModal).
 *   - S-2 Sửa: stash the row as the edit-entry (setExtBlockEditEntry), close
 *     this manager, open the EXT-02 builder prefilled (update-by-id on save).
 *   - S-3 Xóa: window.confirm → removeCustomBlock (localStorage) →
 *     extBlockDeleted toast → live re-render.
 *
 * Reads the live list straight from localStorage and mirrors it to local state
 * so a delete re-renders immediately. React 16 class component (no hooks).
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'openblock-vm';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import {
    closeExtBlockManager,
    openBlockBuilder,
    setExtBlockEditEntry
} from '../reducers/modals';
import {showAlertWithTimeout} from '../reducers/alerts';
import {readCustomBlocks, removeCustomBlock} from '../lib/mstem-extension-storage.js';
import {registerOrRefreshCustomBlocks} from '../lib/mstem-custom-blocks-extension.js';

import ExtManagerModalComponent from '../components/ext-manager-modal/ext-manager-modal.jsx';

const messages = defineMessages({
    title: {
        defaultMessage: 'Quản lý khối mở rộng',
        description: 'UC-47 block manager modal title',
        id: 'gen.ext.manage.block.title'
    },
    subtitle: {
        defaultMessage: 'Các khối bạn đã khai báo (mô phỏng — lưu trên trình duyệt).',
        description: 'UC-47 block manager subtitle',
        id: 'gen.ext.manage.block.subtitle'
    },
    colName: {
        defaultMessage: 'Tên',
        description: 'UC-47 block table column — name',
        id: 'gen.ext.manage.block.colName'
    },
    colShape: {
        defaultMessage: 'Kiểu khối',
        description: 'UC-47 block table column — shape',
        id: 'gen.ext.manage.block.colShape'
    },
    empty: {
        defaultMessage: 'Chưa có khối nào được khai báo.',
        description: 'UC-47 block manager empty state',
        id: 'gen.ext.manage.block.empty'
    },
    confirmDelete: {
        defaultMessage: 'Xóa khối này khỏi danh sách?',
        description: 'UC-47 S-3 delete confirm',
        id: 'gen.ext.manage.block.confirmDelete'
    },
    shapeCommand: {
        defaultMessage: 'Lệnh',
        description: 'UC-47 block shape — command',
        id: 'gen.ext.manage.block.shape.command'
    },
    shapeReporter: {
        defaultMessage: 'Báo cáo',
        description: 'UC-47 block shape — reporter',
        id: 'gen.ext.manage.block.shape.reporter'
    },
    shapeBoolean: {
        defaultMessage: 'Điều kiện',
        description: 'UC-47 block shape — boolean',
        id: 'gen.ext.manage.block.shape.boolean'
    },
    shapeHat: {
        defaultMessage: 'Sự kiện',
        description: 'UC-47 block shape — hat',
        id: 'gen.ext.manage.block.shape.hat'
    },
    none: {
        defaultMessage: '(không)',
        description: 'UC-47 block table — empty value',
        id: 'gen.ext.manage.block.none'
    }
});

const SHAPE_MSG = {
    command: messages.shapeCommand,
    reporter: messages.shapeReporter,
    boolean: messages.shapeBoolean,
    hat: messages.shapeHat
};

class ExtBlockManagerModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClickEdit',
            'handleClickDelete',
            'handleSearchChange',
            'handleRequestClose',
            'renderShapeCell'
        ]);
        this.state = {
            blocks: readCustomBlocks(),
            search: ''
        };
    }
    refreshList () {
        this.setState({blocks: readCustomBlocks()});
    }
    renderShapeCell (row) {
        const {intl} = this.props;
        const msg = SHAPE_MSG[row.shape] || messages.none;
        return intl.formatMessage(msg);
    }
    buildColumns () {
        const {intl} = this.props;
        return [
            {key: 'name', label: intl.formatMessage(messages.colName)},
            {key: 'shape', label: intl.formatMessage(messages.colShape), width: '160px', render: this.renderShapeCell}
        ];
    }
    handleClickEdit (row) {
        // Stash the entry, close the manager, open the EXT-02 builder prefilled.
        this.props.onSetEditEntry(row);
        this.props.onClose();
        this.props.onOpenBuilder();
    }
    handleClickDelete (row) {
        const msg = this.props.intl.formatMessage(messages.confirmDelete);
        // eslint-disable-next-line no-alert
        if (typeof window !== 'undefined' && window.confirm && !window.confirm(msg)) {
            return;
        }
        removeCustomBlock(row.id);
        this.refreshList();
        // Refresh the palette so the deleted block disappears from the category.
        registerOrRefreshCustomBlocks(this.props.vm);
        showAlertWithTimeout(this.props.dispatch, 'extBlockDeleted');
    }
    handleSearchChange (value) {
        this.setState({search: value});
    }
    handleRequestClose () {
        this.props.onClose();
    }
    render () {
        const {intl} = this.props;
        return (
            <ExtManagerModalComponent
                columns={this.buildColumns()}
                emptyMessage={intl.formatMessage(messages.empty)}
                rows={this.state.blocks}
                searchValue={this.state.search}
                subtitle={intl.formatMessage(messages.subtitle)}
                title={intl.formatMessage(messages.title)}
                onClickDelete={this.handleClickDelete}
                onClickEdit={this.handleClickEdit}
                onRequestClose={this.handleRequestClose}
                onSearchChange={this.handleSearchChange}
            />
        );
    }
}

ExtBlockManagerModal.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpenBuilder: PropTypes.func.isRequired,
    onSetEditEntry: PropTypes.func.isRequired,
    vm: PropTypes.instanceOf(VM)
};

const mapStateToProps = state => ({
    vm: state.scratchGui.vm
});

const mapDispatchToProps = dispatch => ({
    dispatch,
    onClose: () => dispatch(closeExtBlockManager()),
    onOpenBuilder: () => dispatch(openBlockBuilder()),
    onSetEditEntry: entry => dispatch(setExtBlockEditEntry(entry))
});

export default compose(
    injectIntl,
    connect(mapStateToProps, mapDispatchToProps)
)(ExtBlockManagerModal);
